import { Suspense, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { getOrganizations } from 'app/modules/organizations/core/requests';

import { I18nProvider } from '../theme/i18n/i18nProvider';
import { MasterInit } from '../theme/layout/MasterInit';
import { LayoutProvider, LayoutSplashScreen } from '../theme/layout/core';
import { ReactComponent as IconToastError } from './assets/icons/icon-toast-error.svg';
import { ReactComponent as IconToastInfo } from './assets/icons/icon-toast-info.svg';
import { ReactComponent as IconToastSuccess } from './assets/icons/icon-toast-success.svg';
import { ReactComponent as IconToastWarning } from './assets/icons/icon-toast-warning.svg';
import { AuthInit } from './modules/auth';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const renderIcon = (value: { theme: string; type: string }) => {
  switch (value.type) {
    case 'info':
      return <IconToastInfo />;
    case 'warning':
      return <IconToastWarning />;
    case 'error':
      return <IconToastError />;
    default:
      return <IconToastSuccess />;
  }
};

const App = () => {
  const location = useLocation();
  const organization = useOrganizationStore((state) => state.state.organization);
  const previousOrgIdRef = useRef<number | null>(organization?.id || null);
  const previousPathRef = useRef<string>(location.pathname);

  useEffect(() => {
    const match = location.pathname.match(/\/org\/(\d+)/);
    const urlOrgId = match ? Number(match[1]) : null;

    if (!urlOrgId || organization?.id === urlOrgId) {
      previousOrgIdRef.current = organization?.id || null;
      previousPathRef.current = location.pathname;
      return;
    }

    const organizationList = useOrganizationStore.getState().getOrganizationList();
    const orgFromList = organizationList?.find((org) => org.id === urlOrgId);

    if (orgFromList) {
      useOrganizationStore
        .getState()
        .setOrganization(orgFromList.id, orgFromList.name, orgFromList.logo || '');
      previousOrgIdRef.current = organization?.id || null;
      previousPathRef.current = location.pathname;
      return;
    }

    // Fetch organization data if not in list
    getOrganizations('page=1&limit=9999999999')
      .then((response) => {
        if (!response.data?.items) return;

        const orgs = response.data.items;
        useOrganizationStore.getState().setOrganizationList(orgs);

        const targetOrg = orgs.find((org) => org.id === urlOrgId);
        const orgToSet = targetOrg || {
          id: urlOrgId,
          name: `Organization ${urlOrgId}`,
          logo: undefined,
        };

        useOrganizationStore
          .getState()
          .setOrganization(orgToSet.id, orgToSet.name, orgToSet.logo || '');
      })
      .catch(() => {
        useOrganizationStore
          .getState()
          .setOrganization(
            urlOrgId,
            organization?.name || 'Organization Name',
            organization?.logo || '',
          );
      });

    // Update refs
    previousOrgIdRef.current = organization?.id || null;
    previousPathRef.current = location.pathname;
  }, [location.pathname, organization?.id]);

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <I18nProvider>
        <LayoutProvider>
          <AuthInit>
            <Outlet />
            <MasterInit />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss={false}
              draggable
              pauseOnHover={false}
              theme="colored"
              className="custom-toast-body"
              icon={renderIcon}
            />
          </AuthInit>
        </LayoutProvider>
      </I18nProvider>
    </Suspense>
  );
};

export { App };
